import React, { forwardRef, useMemo, useRef, useState } from 'react'
import { useHover, usePress } from '@react-aria/interactions'
import styles from './HoverActionOverlay.module.scss'
import { mergeProps } from '@react-aria/utils'
import cx from 'classnames'
import { Button } from 'blaze-ui/Button'
import { ButtonProps } from 'blaze-ui/Button/Types'

export type HoverActionOverlayProps = {
  children: React.ReactNode
  onAction?: () => void
  onPress?: () => void
  buttonLabel?: string
  position?: 'top' | 'center' | 'bottom' | 'top-right'
  className?: string
  scrimClassName?: string
  borderRadius?: number | string
  overlayClassName?: string
  buttonBackgroundClassName?: string
  /** 0–100 (default 60) */
  scrimOpacity?: number
  buttonProps?: ButtonProps
  button?: React.ReactNode
  onClose?: () => void
  padding?: number | string
}

/**
 * HoverActionOverlay
 *
 * Wrap arbitrary content and show a dark scrim + action button
 * on hover or keyboard focus.
 */
export const HoverActionOverlay = forwardRef<HTMLDivElement, HoverActionOverlayProps>(
  (
    {
      children,
      onAction,
      onPress,
      buttonLabel,
      position = 'center',
      className,
      scrimClassName,
      borderRadius,
      overlayClassName,
      buttonBackgroundClassName,
      scrimOpacity = 60,
      buttonProps,
      button,
      padding,
    },
    ref,
  ) => {
    const localRef = useRef<HTMLDivElement>(null)
    const wrapperRef = (ref as React.MutableRefObject<HTMLDivElement | null>) || localRef

    const [isHovered, setIsHovered] = useState(false)

    const { hoverProps } = useHover({
      onHoverStart: () => setIsHovered(true),
      onHoverEnd: () => setIsHovered(false),
    })
    const { pressProps } = usePress({ onPress, isDisabled: !onPress })

    const show = isHovered

    const yPlacementClass = useMemo(() => {
      switch (position) {
        case 'top':
          return styles.alignTop
        case 'top-right':
          return styles.alignTopRight
        case 'bottom':
          return styles.alignBottom
        default:
          return styles.alignCenter
      }
    }, [position])

    const scrimOpacityClamped = Math.min(100, Math.max(0, scrimOpacity)) / 100

    return (
      <div
        {...mergeProps(hoverProps, pressProps)}
        ref={wrapperRef}
        role="group"
        className={cx(styles.wrapper, className)}
        style={{ borderRadius }}
      >
        <div className={styles.content}>{children}</div>

        <div aria-hidden={!show} className={cx(styles.overlay, show && styles.overlayShown, overlayClassName)}>
          <div className={cx(styles.scrim, scrimClassName)} style={{ opacity: show ? scrimOpacityClamped : 0 }} />

          <div className={cx(styles.rail, yPlacementClass)} style={{ padding }}>
            {(buttonLabel || button) && (
              <div className={styles.buttonContainer}>
                <div className={cx(styles.buttonBackground, buttonBackgroundClassName)} />
                {button || (
                  <Button {...buttonProps} onPress={onAction}>
                    {buttonLabel}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  },
)

HoverActionOverlay.displayName = 'HoverActionOverlay'
